/**
 * Atividade recente: as ultimas ligacoes da IA e mensagens de WhatsApp.
 *
 * Movido de pages/Dashboard.tsx para o modulo. Decisao nao obvia: nome do
 * lead passa por `shortenName` e telefone por `maskPhoneDisplay` mesmo aqui,
 * numa lista de seis itens -- o painel fica aberto em tela de escritorio o
 * dia inteiro e ninguem precisa do numero completo para acompanhar a fila.
 */

import { StatusPill } from '../../../components/LeadsTable';
import { ErrorPanel } from '../../../components/PageState';
import { Icon, LinkButton, SectionCard } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import {
  campaignStatusTone,
  CAMPAIGN_STATUS_LABEL,
  CHANNEL_LABEL,
  formatBRLFromCents,
  formatDateTime,
  maskPhoneDisplay,
  shortenName,
} from '../../../lib/format';
import type { CampaignListItem, Page } from '../../../types';

export function AtividadeRecente({
  state,
  onRetry,
}: {
  readonly state: AsyncState<Page<CampaignListItem>>;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <SectionCard
      id="atividade-recente"
      title="Atividade recente"
      icon="telefone"
      description="Últimas ligações da IA e mensagens de WhatsApp no período."
      aside={
        <LinkButton to="/campanhas" variant="ghost" size="sm" iconRight="seta-dir">
          Ver todas
        </LinkButton>
      }
    >
      {state.status === 'loading' ? (
        <ul className="flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-12 animate-pulse rounded-lg bg-ink/5" />
          ))}
        </ul>
      ) : null}
      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar a atividade" error={state.error} onRetry={onRetry} /> : null}
      {state.status === 'success' ? (
        state.data.items.length === 0 ? (
          <p className="py-4 text-sm text-ink-subtle">Nenhum acionamento no período.</p>
        ) : (
          <ul className="divide-y divide-line/60">
            {state.data.items.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${c.channel === 'voice' ? 'bg-series-1/10 text-series-1' : 'bg-series-3/10 text-series-3'}`}
                  >
                    <Icon name={c.channel === 'voice' ? 'telefone' : 'whatsapp'} size="sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">
                      {shortenName(c.leadName)} <span className="font-normal text-ink-subtle">· {maskPhoneDisplay(c.phoneE164)}</span>
                    </p>
                    <p className="truncate text-xs text-ink-subtle">
                      {CHANNEL_LABEL[c.channel]} · {formatBRLFromCents(c.amountCents)}
                      {c.converted ? ' · recuperado' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill tone={campaignStatusTone(c.status)} label={CAMPAIGN_STATUS_LABEL[c.status]} />
                  <span className="tabular-nums text-xs text-ink-subtle">{formatDateTime(c.createdAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </SectionCard>
  );
}
